import React from 'react';
import { useAuth0Context } from '../contexts/Auth0Context';
import { Building2, Cog, Users, Shield, ArrowRight } from 'lucide-react';
import heroBackground from '../assets/images/hero-background.png';
import procogiaLogo from '../assets/images/procogia-logo-light.png';

const ProfileSelector = () => {
  const { userProfiles, selectProfile, auth0User, auth0IsLoading } = useAuth0Context();

  const getRoleIcon = (role) => {
    switch (role) {
      case 'operations_manager':
        return <Building2 className="h-5 w-5" />;
      case 'maintenance_engineer':
        return <Cog className="h-5 w-5" />;
      case 'plant_supervisor':
        return <Users className="h-5 w-5" />;
      case 'admin':
        return <Shield className="h-5 w-5" />;
      default:
        return <Building2 className="h-5 w-5" />;
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'operations_manager':
        return { bg: '#1fb8cc', hover: '#000000' };
      case 'maintenance_engineer':
        return { bg: '#95c100', hover: '#000000' };
      case 'plant_supervisor':
        return { bg: '#1fb8cc', hover: '#000000' };
      case 'admin':
        return { bg: '#95c100', hover: '#000000' };
      default:
        return { bg: '#65676e', hover: '#000000' };
    }
  };

  const getRoleTitle = (role) => {
    switch (role) {
      case 'operations_manager':
        return 'Operations Manager';
      case 'maintenance_engineer':
        return 'Maintenance Engineer';
      case 'plant_supervisor':
        return 'Plant Supervisor';
      case 'admin':
        return 'System Administrator';
      default:
        return 'User';
    }
  };

  if (auth0IsLoading) {
    return (
      <div 
        className="min-h-screen bg-white relative"
        style={{
          backgroundImage: `url(${heroBackground}), url('https://procogia.com/wp-content/uploads/2025/04/hero-background.png')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <div className="relative z-10 min-h-screen flex items-center justify-center">
          <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md text-center">
            <div className="flex items-center justify-center mb-6">
              <div className="flex items-center space-x-3">
                                        <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: '#95c100' }}>
                  <div className="w-6 h-6 bg-white rounded-full flex items-center justify-center">
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  </div>
                </div>
                                        <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: '#1fb8cc' }}>
                  <div className="w-6 h-6 bg-white rounded-full flex items-center justify-center">
                    <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                  </div>
                </div>
              </div>
              <span className="ml-3 text-2xl font-bold text-gray-900">PROCOGIA</span>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Loading Platform</h1>
            <p className="text-gray-600 mb-8">Advanced AI-Powered Manufacturing Intelligence</p>
            <div className="w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading secure authentication...</p>
            <p className="text-gray-500 text-sm mt-2">Please wait while we prepare your experience</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen bg-white relative"
      style={{
        backgroundImage: `url(${heroBackground}), url('https://procogia.com/wp-content/uploads/2025/04/hero-background.png')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      {/* Content */}
      <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-2xl p-8 w-full max-w-lg" style={{ border: '1px solid #c9cace' }}>
          <div className="text-center mb-8">
            {/* ProCogia Logo */}
            <div className="flex items-center justify-center mb-6">
              <img
                src={procogiaLogo}
                alt="ProCogia Logo"
                className="h-12 w-auto"
                                          onError={(e) => {
                            e.target.src = "https://procogia.com/wp-content/uploads/2024/03/procogia-horizontal-light-bg-1-2048x487.png";
                          }}
              />
            </div>

            <h1 className="text-2xl font-bold mb-2" style={{ color: '#333333' }}>Select Your Role</h1>
            <p className="mb-6" style={{ color: '#333333' }}>Advanced AI-Powered Manufacturing Intelligence</p>

            {/* Welcome message */}
            <div className="flex items-center justify-center mb-6">
              {auth0User?.picture && (
                <img
                  src={auth0User.picture}
                  alt={auth0User.name}
                  className="w-12 h-12 rounded-full border-2 border-gray-200 mr-3"
                />
              )}
              <div className="text-left">
                <p className="text-sm" style={{ color: '#65676e' }}>Welcome back,</p>
                <p className="font-semibold" style={{ color: '#333333' }}>{auth0User?.given_name || auth0User?.name}</p>
              </div>
            </div>
          </div>

          {/* Role Selection */}
          <div className="space-y-3">
            {userProfiles && userProfiles.length > 0 ? (
              userProfiles.map((profile, index) => (
                <button
                  key={index}
                  onClick={() => selectProfile(profile)}
                  className="group w-full text-white p-4 rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-lg"
                  style={{
                    backgroundColor: getRoleColor(profile.role).bg,
                    border: '1px solid #c9cace'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = getRoleColor(profile.role).hover}
                  onMouseLeave={(e) => e.target.style.backgroundColor = getRoleColor(profile.role).bg}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}>
                        {getRoleIcon(profile.role)}
                      </div>
                      <div className="text-left">
                        <h3 className="font-semibold" style={{ color: '#ffffff' }}>{getRoleTitle(profile.role)}</h3>
                        <p className="text-sm" style={{ color: 'rgba(255,255,255,0.8)' }}>{profile.name}</p>
                        <p className="text-xs" style={{ color: 'rgba(255,255,255,0.6)' }}>{profile.department}</p>
                      </div>
                    </div>
                    <ArrowRight className="h-5 w-5 transform group-hover:translate-x-1 transition-transform" />
                  </div>
                </button>
              ))
            ) : (
              <div className="text-center py-8">
                <p style={{ color: '#65676e' }}>No profiles available</p>
              </div>
            )}
          </div>

          <div className="mt-8 pt-6 text-center" style={{ borderTop: '1px solid #c9cace' }}>
            <p className="text-xs" style={{ color: '#65676e' }}>Secure role-based access powered by Auth0</p>
            <p className="text-xs mt-1" style={{ color: '#65676e' }}>Enterprise-grade security with multi-factor authentication</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileSelector; 